import { AttributeValueInput } from "../types";
import { MappingContext } from "../orchestrator";
import { withScope, captureException } from "@sentry/react";
import { memoizeMapperFactory } from "../utils/memoize";
import { z } from "zod";

/**
 * Schema for conditionGridMapper configuration.
 * Maps condition response values to their attribute mappings.
 */
const conditionGridMapperConfigSchema = z.record(
  z.string(),
  z.array(
    z.object({
      attributeDefinitionId: z.number(),
      booleanAttributeValue: z.boolean().optional(),
      stringAttributeValue: z.string().optional(),
      integerAttributeValue: z.number().optional(),
    })
  )
);

/**
 * Schema for the JSON-stringified response from a condition grid question.
 * The response is expected to be a JSON string that parses into an object
 * with string keys (row labels) and string values (selected options).
 */
const conditionGridResponseSchema = z.record(z.string(), z.string());

/**
 * Internal implementation of conditionGridMapper without memoization.
 */
function conditionGridMapperImpl(config: unknown) {
  const parsedConfig = conditionGridMapperConfigSchema.parse(config);

  return (response: string, _context: MappingContext): AttributeValueInput[] => {
    const attributes: AttributeValueInput[] = [];

    if (typeof response !== "string") {
      return [];
    }

    try {
      // Parse the JSON string and validate its structure
      const answers = conditionGridResponseSchema.parse(JSON.parse(response));

      // Process each answer in the grid
      Object.values(answers).forEach((selectedValue) => {
        // Look up the attribute mapping for this value
        const mapping = parsedConfig[selectedValue];
        
        if (mapping) {
          // Add the mapped attributes with isSkipped: false
          attributes.push(
            ...mapping.map((attr) => ({ ...attr, isSkipped: false }))
          );
        }
      });
    } catch (e) {
      console.error("Failed to parse condition grid response:", response, e);

      withScope((scope) => {
        scope.setTag("location", "conditionGridMapper");
        scope.setContext("mapperContext", {
          response,
          configKeys: Object.keys(parsedConfig),
        });
        captureException(e);
      });
    }

    return attributes;
  };
}

/**
 * Creates a mapper for condition grid questions (like mental health conditions).
 * It parses JSON-stringified grid answers and maps selected values to attributes.
 * 
 * @param config - A map where keys are the possible response values (e.g., "schizophrenia_yes", "bipolar_no")
 *                 and values are arrays of attributes to set when that option is selected.
 * 
 * @example
 * ```typescript
 * conditionGridMapper({
 *   schizophrenia_yes: [{ attributeDefinitionId: 123, booleanAttributeValue: true }],
 *   schizophrenia_no: [{ attributeDefinitionId: 123, booleanAttributeValue: false }],
 *   bipolar_yes: [{ attributeDefinitionId: 456, booleanAttributeValue: true }],
 *   bipolar_no: [{ attributeDefinitionId: 456, booleanAttributeValue: false }],
 * })
 * ```
 * 
 * This function is memoized to avoid repeated config parsing.
 */
export const conditionGridMapper = memoizeMapperFactory(conditionGridMapperImpl);